import { observer } from 'mobx-react-lite';
import {
  List,
  MagnifyingGlass,
  Play,
  Pause,
  SkipBack,
  SkipForward,
  ArrowCounterClockwise,
  ArrowClockwise,
} from '@phosphor-icons/react';
import { useResolve } from '@/infrastructure/useResolve';
import { TOKEN_II18n } from '@/services/i18n/I18n';
import { TOKEN_PlayerStore } from './PlayerStore';
import { SpeedSelectorView } from './SpeedSelectorView';
import { VoiceSelectorView } from './VoiceSelectorView';
import { ThemeToggleView } from './ThemeToggleView';

const iconBtn =
  'flex h-11 w-11 items-center justify-center rounded-full text-muted transition-colors hover:bg-line/60 hover:text-ink';

export const PlayerView = observer(function PlayerView() {
  const store = useResolve(TOKEN_PlayerStore);
  const i18n = useResolve(TOKEN_II18n);

  return (
    <div className="flex min-h-0 flex-1 flex-col pb-6">
      <div className="flex items-center gap-2">
        <button type="button" onClick={store.toggleChapterList} className={iconBtn} title={i18n.t('player.chapters')}>
          <List size={20} />
        </button>
        <h2 className="flex-1 truncate text-center font-serif text-base font-medium text-ink" title={store.title}>
          {store.title}
        </h2>
        {/* Search on top, theme toggle beneath it, so each side is one column wide and the title
            stays centered. */}
        <div className="flex flex-col items-center gap-1">
          <button type="button" onClick={store.toggleSearch} className={iconBtn} title={i18n.t('player.search')}>
            <MagnifyingGlass size={20} />
          </button>
          <ThemeToggleView />
        </div>
      </div>

      <div className="mx-auto mt-3 w-full max-w-md">
        <div className="h-px w-full bg-line">
          <div
            className="h-px bg-accent transition-all"
            style={{ width: `${store.chapterProgressPercent}%` }}
          />
        </div>
        <p className="mt-2 truncate text-center text-xs tracking-wide text-muted">
          {store.currentChapterLabel} · {store.chapterProgressPercent}%
        </p>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto py-8">
        {/* m-auto centers a short paragraph but collapses to the top for a tall one, so the start
            stays reachable (items-center would clip the top out of the scroll area). */}
        <p className="m-auto max-w-prose text-center font-serif text-2xl leading-loose text-balance text-ink">
          {store.currentParagraphText}
        </p>
      </div>

      <div className="flex items-center justify-center gap-3">
        <button type="button" onClick={store.previousChapter} className={iconBtn} title={i18n.t('player.prevChapter')}>
          <SkipBack size={20} weight="fill" />
        </button>
        <button type="button" onClick={store.skipBackward} className={iconBtn} title={i18n.t('player.back')}>
          <ArrowCounterClockwise size={22} />
        </button>
        <button
          type="button"
          onClick={store.togglePlay}
          className="flex h-16 w-16 items-center justify-center rounded-full bg-accent text-paper shadow-sm transition-colors hover:bg-accent-strong"
          title={store.isPlaying ? i18n.t('player.pause') : i18n.t('player.play')}
          aria-label={store.isPlaying ? i18n.t('player.pause') : i18n.t('player.play')}
        >
          {store.isPlaying ? (
            <Pause size={26} weight="fill" />
          ) : (
            <Play size={26} weight="fill" className="ml-0.5" />
          )}
        </button>
        <button type="button" onClick={store.skipForward} className={iconBtn} title={i18n.t('player.forward')}>
          <ArrowClockwise size={22} />
        </button>
        <button type="button" onClick={store.nextChapter} className={iconBtn} title={i18n.t('player.nextChapter')}>
          <SkipForward size={20} weight="fill" />
        </button>
      </div>

      <div className="mt-5 flex items-center justify-center gap-3">
        <SpeedSelectorView />
        <VoiceSelectorView />
      </div>
    </div>
  );
});
